import { useForm, getFormProps } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import { invariantResponse } from "@epic-web/invariant";
import { redirect, useFetcher, useFetchers, useLocation } from "react-router";
import { ServerOnly } from "remix-utils/server-only";
import { z } from "zod";
import { Icon } from "#app/components/ui/icon.tsx";
import { useHints, useOptionalHints } from "#app/utils/client-hints.tsx";
import {
  useOptionalRequestInfo,
  useRequestInfo,
} from "#app/utils/request-info.ts";
import { type Theme, setTheme } from "#app/utils/theme.server.ts";
import { type Route } from "./+types/theme-switch.ts";

export const handle = {
  getSitemapEntries: () => null,
};

const ThemeFormSchema = z.object({
  theme: z.enum(["system", "light", "dark"]),
  redirectTo: z.string().optional(),
});

export async function loader() {
  return redirect("/", { status: 404 });
}

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData();
  const submission = parseWithZod(formData, {
    schema: ThemeFormSchema,
  });

  invariantResponse(submission.status === "success", "Invalid theme received");

  const { theme, redirectTo } = submission.value;

  console.log(
    `Theme switch action called with theme: ${theme}, redirectTo: ${redirectTo}`,
  );
  const responseInit = {
    headers: { "set-cookie": setTheme(theme) },
  };

  return redirect(redirectTo || "/", responseInit);
}

const iconTransformOrigin = { transformOrigin: "50% 100px" };

export function ThemeSwitch({
  userPreference,
}: {
  userPreference?: Theme | null;
}) {
  const fetcher = useFetcher<typeof action>();
  const requestInfo = useRequestInfo();
  const location = useLocation();

  const [form] = useForm({
    id: "theme-switch",
  });

  const optimisticMode = useOptimisticThemeMode();
  const mode = optimisticMode ?? userPreference ?? "system";
  const nextMode =
    mode === "system" ? "light" : mode === "light" ? "dark" : "system";

  const iconSpanClassName =
    "absolute inset-0 flex items-center justify-center transition-transform duration-500 motion-reduce:duration-[0s]";

  return (
    <fetcher.Form
      method="POST"
      {...getFormProps(form)}
      action="/resources/theme-switch"
    >
      <ServerOnly>
        {() => (
          <input
            type="hidden"
            name="redirectTo"
            value={
              typeof window !== "undefined"
                ? window.location.pathname
                : requestInfo.path
            }
          />
        )}
      </ServerOnly>
      <input type="hidden" name="theme" value={nextMode} />
      <input
        type="hidden"
        name="redirectTo"
        value={location.pathname + location.search}
      />
      <button
        type="submit"
        className="flex size-9 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-border bg-background p-1 transition-colors hover:bg-muted"
        aria-label={`Switch to ${nextMode} mode`}
      >
        <div className="relative size-5">
          {/* Moon — swings in from top when dark */}
          <span
            className={`${iconSpanClassName} ${mode === "dark" ? "rotate-0" : "rotate-90"}`}
            style={iconTransformOrigin}
          >
            <Icon name="moon" className="size-4" />
          </span>
          {/* Sun — swings in from bottom when light */}
          <span
            className={`${iconSpanClassName} ${mode === "light" ? "rotate-0" : "-rotate-90"}`}
            style={iconTransformOrigin}
          >
            <Icon name="sun" className="size-4" />
          </span>
          {/* Laptop — slides up when system */}
          <span
            className={`absolute inset-0 flex items-center justify-center transition-transform duration-500 motion-reduce:duration-[0s] ${mode === "system" ? "translate-y-0" : "translate-y-7"}`}
          >
            <Icon name="laptop" className="size-4" />
          </span>
        </div>
      </button>
    </fetcher.Form>
  );
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
  const fetchers = useFetchers();
  const themeFetcher = fetchers.find(
    (f) => f.formAction === "/resources/theme-switch",
  );

  if (themeFetcher && themeFetcher.formData) {
    const submission = parseWithZod(themeFetcher.formData, {
      schema: ThemeFormSchema,
    });

    if (submission.status === "success") {
      return submission.value.theme;
    }
  }
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
  const hints = useHints();
  const requestInfo = useRequestInfo();
  const optimisticMode = useOptimisticThemeMode();
  if (optimisticMode) {
    return optimisticMode === "system" ? hints.theme : optimisticMode;
  }
  return requestInfo.userPrefs.theme ?? hints.theme;
}

export function useOptionalTheme() {
  const optionalHints = useOptionalHints();
  const optionalRequestInfo = useOptionalRequestInfo();
  const optimisticMode = useOptimisticThemeMode();
  if (optimisticMode) {
    return optimisticMode === "system" ? optionalHints?.theme : optimisticMode;
  }
  return optionalRequestInfo?.userPrefs.theme ?? optionalHints?.theme;
}
